import { HintAnimation } from '../hint-motion';
import { hline, text } from '../../academy/films/story-scene';
import { abs, C, glass, icon, orbiTemplate } from './scene-kit';
import { CONTENT, HintStory, navPoint, portalShell } from './hint-story';

/**
 * Hinweis B „Lerne AlphaLearn in der Academy kennen“.
 *
 * Didaktik: Neue Nutzer unterschätzen, wie kurz eine Lektion ist. Der Film
 * zeigt deshalb den ganzen Kreislauf einer Lektion in einem Atemzug:
 * WARUM „Lerne AlphaLearn kennen — in kurzen Lektionen“ → WO Menüpunkt
 * Academy → WAS „Lektion starten“, Video läuft, eine Quizfrage wird
 * richtig beantwortet → GESCHAFFT der Führerschein-Fortschritt wächst und
 * Orbi freut sich. Die Botschaft: wenige Minuten, sofort ein Erfolg.
 */

const LESSON = { x: 182, y: 66, w: 438, h: 264 };
const PLAY: [number, number] = [CONTENT.x + 20 + 348, CONTENT.y + 170 + 47];
const OPTION: [number, number] = [LESSON.x + 232 + 96, LESSON.y + 116 + 14];

const agentsPage =
  `<div data-r="pageA">` +
  text(20, 12, 300, 'KI-Agenten', 18, 400) +
  hline(16, 46, 428) +
  ['Statistik-Tutor', 'Mathe-Tutor']
    .map((name, i) =>
      glass(
        18,
        0.5,
        `left:${20 + i * 140}px;top:64px;width:128px;height:96px;`,
        `<div style="${abs(46, 12, 36, 36)}">${orbiTemplate(`tile${i}`, 36)}</div>` +
          text(8, 60, 112, name, 12, 600, C.text, 'text-align:center;'),
      ),
    )
    .join('') +
  `</div>`;

const topic = (i: number, ic: string, name: string, fill: number) =>
  glass(
    16,
    0.55,
    `left:${20 + i * 142}px;top:76px;width:130px;height:78px;`,
    `<div style="${abs(12, 12, 22, 22)}">${icon(ic, 20, C.action)}</div>` +
      text(12, 38, 110, name, 12, 600) +
      `<div style="${abs(12, 62, 106, 4, 'border-radius:999px;background:rgb(83 109 145 / .16);overflow:hidden;')}"><div style="width:${fill}%;height:100%;background:${C.accent};border-radius:999px;"></div></div>`,
  );

const academyPage =
  `<div data-r="pageB" style="opacity:0;">` +
  text(20, 12, 200, 'Academy', 18, 400) +
  text(20, 40, 300, 'Kurze Lektionen mit Video und Quiz', 11.5, 500, C.muted) +
  // Führerschein-Fortschritt oben rechts
  `<div style="${abs(300, 18, 140, 30)}">` +
  text(0, 0, 140, 'Führerschein', 11, 600, C.muted) +
  `<div style="${abs(0, 20, 140, 6, 'border-radius:999px;background:rgb(83 109 145 / .16);overflow:hidden;')}"><div data-r="prog" style="width:100%;height:100%;border-radius:999px;background:${C.accent};transform-origin:0 50%;transform:scale(.06);"></div></div>` +
  `</div>` +
  hline(16, 62, 428) +
  topic(0, 'help', 'Erste Schritte', 0) +
  topic(1, 'agent', 'KI-Agenten', 0) +
  topic(2, 'brain', 'Wissensbasis', 0) +
  glass(
    18,
    0.6,
    'left:20px;top:170px;width:420px;height:94px;',
    text(16, 14, 300, 'Empfohlene nächste Lektion', 11, 600, C.muted) +
      text(16, 32, 260, 'Das Portal kennenlernen', 15, 600) +
      text(16, 58, 260, '3 Min. · Video · Quiz', 11.5, 500, C.muted) +
      `<div data-r="play" style="${abs(290, 31, 116, 32, `border-radius:999px;background:${C.action};color:#fff;display:flex;align-items:center;justify-content:center;gap:6px;font-size:12px;font-weight:600;box-shadow:0 8px 16px -8px rgb(37 99 235 / .8);white-space:nowrap;`)}">${icon('play', 13, '#fff', 2)}Lektion starten</div>`,
  ) +
  `</div>`;

const option = (y: number, label: string, ok = false) =>
  `<div style="${abs(232, y, 192, 28, `border-radius:999px;background:rgb(255 255 255 / .62);box-shadow:inset 0 0 0 1px rgb(83 109 145 / .2);`)}">` +
  (ok
    ? `<div data-r="optOk" style="position:absolute;inset:0;border-radius:999px;opacity:0;background:rgb(0 163 92 / .14);box-shadow:inset 0 0 0 1.5px ${C.ok};"></div>`
    : '') +
  `<div style="position:relative;height:100%;display:flex;align-items:center;justify-content:space-between;padding:0 12px;font-size:12px;font-weight:500;color:${C.text};">${label}` +
  (ok ? `<span data-r="optCheck" style="opacity:0;">${icon('check', 15, C.okInk, 2.4)}</span>` : '') +
  `</div></div>`;

const lesson =
  `<div data-r="lesson" style="opacity:0;">` +
  glass(
    22,
    0.92,
    `left:${LESSON.x}px;top:${LESSON.y}px;width:${LESSON.w}px;height:${LESSON.h}px;`,
    text(16, 12, 400, 'Lektion · Das Portal kennenlernen', 13, 600) +
      // Video
      `<div style="${abs(16, 40, 200, 112, 'border-radius:14px;overflow:hidden;background:linear-gradient(135deg,#dbe6f7,#f3e8ff);')}">` +
      `<div style="${abs(82, 34, 36, 36)}">${orbiTemplate('vid', 36)}</div>` +
      `<div style="${abs(10, 98, 180, 4, 'border-radius:999px;background:rgb(255 255 255 / .7);overflow:hidden;')}"><div data-r="vidBar" style="width:100%;height:100%;background:${C.action};transform-origin:0 50%;transform:scale(0);"></div></div>` +
      `</div>` +
      // Quiz
      `<div style="${abs(232, 40, 192, 40, `font-size:12.5px;font-weight:600;line-height:1.35;color:${C.text};`)}">Wo findest du die Hilfe zu einer Seite?</div>` +
      option(84, 'Im Profilmenü') +
      option(116, 'Am Fragezeichen oben', true) +
      option(148, 'In den Einstellungen') +
      // Erfolg
      `<div data-r="badge" style="${abs(16, 178, 300, 60, 'opacity:0;display:flex;align-items:center;gap:12px;transform-origin:30px 30px;')}">` +
      `<div style="width:52px;height:52px;border-radius:999px;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle at 30% 25%, #fff, rgb(219 230 247) 55%, rgb(147 197 253));box-shadow:0 8px 16px -8px rgb(37 99 235 / .6), inset 0 1px 2px #fff;">${icon('award', 26, C.action, 2)}</div>` +
      `<div><div style="font-size:13px;font-weight:600;color:${C.text};">Lektion geschafft</div>` +
      `<div style="font-size:11.5px;font-weight:500;color:${C.muted};">ein Schritt zum Führerschein</div></div>` +
      `</div>` +
      `<div data-r="lesOrbi" style="${abs(372, 186, 40, 40, 'opacity:0;')}">${orbiTemplate('les', 40)}</div>`,
  ) +
  `</div>`;

export const visitAcademyAnimation = (): HintAnimation =>
  new HintStory({
    intro: { line1: ['Lerne', 'AlphaLearn', 'kennen'], line2: ['in', 'kurzen', 'Lektionen'] },
    portal: portalShell({ active: 0, content: agentsPage + academyPage }) + lesson,
    tracks: [
      { r: 'nav0', at: [1.5, 1.6], o: [1, 0] },
      { r: 'nav4', at: [1.5, 1.6], o: [0, 1] },
      { r: 'pageA', at: [1.55, 1.68], o: [1, 0] },
      { r: 'pageB', at: [1.66, 1.86], o: [0, 1], y: [8, 0] },
      { r: 'lesson', at: [2.26, 2.5], o: [0, 1], x: [30, 0] },
      { r: 'vidBar', at: [2.5, 3.05], s: [0, 1] },
      { r: 'optOk', at: [3.27, 3.38], o: [0, 1] },
      { r: 'optCheck', at: [3.3, 3.42], o: [0, 1], s: [0.5, 1] },
      { r: 'badge', at: [3.52, 3.78], o: [0, 1], s: [0.7, 1] },
      { r: 'lesOrbi', at: [3.58, 3.72], o: [0, 1] },
      { r: 'prog', at: [3.75, 4.1], s: [0.06, 0.14] },
    ],
    glows: [
      { r: 'navGlow4', at: 1.15, dur: 0.45 },
      { r: 'play', at: 1.95, dur: 0.4 },
    ],
    orbis: [
      { prefix: 'vid', pop: 2.55, laugh: 99 },
      { prefix: 'les', pop: 3.6, laugh: 3.7 },
    ],
    cursor: {
      at: [0, 1.1, 1.44, 1.56, 2.12, 2.3, 3.17, 3.34, 3.9, 99],
      x: [300, 300, navPoint(4)[0], navPoint(4)[0], PLAY[0], PLAY[0], OPTION[0], OPTION[0], 560, 560],
      y: [250, 250, navPoint(4)[1], navPoint(4)[1], PLAY[1], PLAY[1], OPTION[1], OPTION[1], 330, 330],
      clicks: [1.5, 2.2, 3.25],
    },
    camera: {
      at: [0, 2.28, 2.55, 3.95, 4.25, 99],
      x: [320, 320, 378, 378, 320, 320],
      y: [176, 176, 198, 198, 176, 176],
      s: [1, 1, 1.22, 1.22, 1, 1],
    },
    still: 3.9,
  });
